import { doc, getDoc, collection, query, orderBy, getDocs, limit } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db, auth } from '../firebaseConfig';
import { secureLog } from '../../lib/errors/logger';
import { isValidDocumentId, validateNumber } from '../../utils/validation';
import { sanitizeError } from '../../utils/sanitization';

// Types
export interface CoinTransaction {
  id: string;
  type: 'Earned' | 'Redeemed' | 'Referral' | 'Bonus';
  amount: number;
  timestamp: Date;
  description: string;
}

export interface UserCoins {
  loyaltyCoins: number;
  tier: 'Bronze' | 'Silver' | 'Gold' | 'Platinum';
  lastUpdated: Date;
}

export interface TierInfo {
  name: 'Bronze' | 'Silver' | 'Gold' | 'Platinum';
  minCoins: number;
  maxCoins: number;
  color: string;
  bgColor: string;
  borderColor: string;
}

// Tier configuration
export const TIER_CONFIG: Record<string, TierInfo> = {
  Bronze: {
    name: 'Bronze',
    minCoins: 0,
    maxCoins: 99,
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200'
  },
  Silver: {
    name: 'Silver',
    minCoins: 100,
    maxCoins: 499,
    color: 'text-gray-700',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200'
  },
  Gold: {
    name: 'Gold',
    minCoins: 500,
    maxCoins: 999,
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200'
  },
  Platinum: {
    name: 'Platinum',
    minCoins: 1000,
    maxCoins: Infinity,
    color: 'text-purple-700',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200'
  }
};

// Get user's coin balance and tier
export const getUserCoins = async (userId: string): Promise<UserCoins> => {
  try {
    // Security: Input validation
    if (!isValidDocumentId(userId)) {
      throw new Error('Invalid user ID');
    }

    // Security: IDOR Protection - Verify userId matches authenticated user
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('Authentication required');
    }

    if (userId !== currentUser.uid) {
      // SECURITY: Prevent IDOR - users can only access their own coins
      secureLog('warn', 'IDOR attempt detected in getUserCoins');
      throw new Error('Access denied');
    }

    const userDoc = doc(db, 'Users', userId);
    const userSnapshot = await getDoc(userDoc);
    
    if (!userSnapshot.exists()) {
      throw new Error('User not found');
    }
    
    const userData = userSnapshot.data();
    const loyaltyCoins = userData.loyaltyCoins || 0;
    
    // Determine tier based on coin count
    let tier: 'Bronze' | 'Silver' | 'Gold' | 'Platinum' = 'Bronze';
    if (loyaltyCoins >= 1000) tier = 'Platinum';
    else if (loyaltyCoins >= 500) tier = 'Gold';
    else if (loyaltyCoins >= 100) tier = 'Silver';
    
    return {
      loyaltyCoins,
      tier,
      lastUpdated: userData.lastUpdated?.toDate() || new Date()
    };
  } catch (error) {
    secureLog('warn', 'Failed to get user coins:', sanitizeError(error));
    throw error;
  }
};

// Get coin transaction history
export const getCoinHistory = async (userId: string): Promise<CoinTransaction[]> => {
  try {
    // Security: Input validation
    if (!isValidDocumentId(userId)) {
      return [];
    }

    // Security: IDOR Protection - Verify userId matches authenticated user
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('Authentication required');
    }

    if (userId !== currentUser.uid) {
      // SECURITY: Prevent IDOR - users can only access their own transaction history
      secureLog('warn', 'IDOR attempt detected in getCoinHistory');
      return [];
    }

    const historyRef = collection(db, 'Users', userId, 'coinsHistory');
    const historyQuery = query(historyRef, orderBy('timestamp', 'desc'), limit(1000)); // Security: Limit results
    const historySnapshot = await getDocs(historyQuery);
    
    const transactions: CoinTransaction[] = [];
    
    historySnapshot.forEach((doc) => {
      const data = doc.data();
      transactions.push({
        id: doc.id,
        type: data.type || 'Earned',
        amount: data.amount || 0,
        timestamp: data.timestamp?.toDate() || new Date(),
        description: data.description || 'Transaction'
      });
    });
    
    return transactions;
  } catch (error) {
    secureLog('warn', 'Failed to get coin history:', sanitizeError(error));
    // Return empty array if subcollection doesn't exist
    return [];
  }
};

// Get tier information for a given coin amount
export const getTierForCoins = (coins: number): TierInfo => {
  // Security: Input validation
  const coinsValidation = validateNumber(coins, 'Coins', 0, 1000000);
  if (!coinsValidation.valid) {
    return TIER_CONFIG.Bronze; // Default to Bronze for invalid input
  }

  if (coins >= 1000) return TIER_CONFIG.Platinum;
  if (coins >= 500) return TIER_CONFIG.Gold;
  if (coins >= 100) return TIER_CONFIG.Silver;
  return TIER_CONFIG.Bronze;
};

// Get progress to next tier
export const getTierProgress = (coins: number): { currentTier: TierInfo; nextTier: TierInfo | null; progress: number } => {
  // Security: Input validation
  const coinsValidation = validateNumber(coins, 'Coins', 0, 1000000);
  if (!coinsValidation.valid) {
    return {
      currentTier: TIER_CONFIG.Bronze,
      nextTier: TIER_CONFIG.Silver,
      progress: 0
    };
  }

  const currentTier = getTierForCoins(coins);
  
  let nextTier: TierInfo | null = null;
  let progress = 0;
  
  if (currentTier.name === 'Bronze') {
    nextTier = TIER_CONFIG.Silver;
    progress = (coins / 100) * 100;
  } else if (currentTier.name === 'Silver') {
    nextTier = TIER_CONFIG.Gold;
    progress = ((coins - 100) / 400) * 100;
  } else if (currentTier.name === 'Gold') {
    nextTier = TIER_CONFIG.Platinum;
    progress = ((coins - 500) / 500) * 100;
  } else {
    // Platinum tier - max level
    progress = 100;
  }
  
  return { currentTier, nextTier, progress };
};

// Get transaction type styling
export const getTransactionTypeStyle = (type: CoinTransaction['type']): { color: string; bgColor: string; borderColor: string } => {
  switch (type) {
    case 'Earned':
      return {
        color: 'text-green-700',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200'
      };
    case 'Redeemed':
      return {
        color: 'text-red-700',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200'
      };
    case 'Referral':
      return {
        color: 'text-blue-700',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200'
      };
    case 'Bonus':
      return {
        color: 'text-purple-700',
        bgColor: 'bg-purple-50',
        borderColor: 'border-purple-200'
      };
    default:
      return {
        color: 'text-gray-700',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-200'
      };
  }
};

// Format date for display
export const formatTransactionDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};
